import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Badge, cn, Icon } from "@sagot/ui";
import { useWorkspaceContext } from "../../../session/workspace-context";
import { useFormatters } from "../shared/format";
import type { ConversationDetail } from "./inbox-model";
import { formatMinutes, slaState } from "./sla";

export function IdentityList({ detail }: { detail: ConversationDetail }) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const v = detail.visitor;
  const rows: [string, string | null][] = [
    [t("engage.inbox.details.identity"), t(`engage.inbox.details.identityTypes.${v.identity_type}`, { defaultValue: v.identity_type })],
    [t("engage.inbox.details.channel"), t(`engage.channels.${v.channel}`, { defaultValue: v.channel })],
    [t("engage.inbox.details.location"), v.location],
    [t("engage.inbox.details.device"), v.device],
    [t("engage.inbox.details.firstSeen"), v.first_seen_at ? fmt.date(v.first_seen_at) : null],
    [t("engage.inbox.details.language"), v.language],
    [t("engage.inbox.details.page"), v.page_url],
  ];
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-2 text-xs">
      {rows
        .filter((row): row is [string, string] => Boolean(row[1]))
        .map(([label, value]) => (
          <div key={label} className="contents">
            <dt className="text-fg-subtle">{label}</dt>
            <dd className="truncate text-right font-semibold">{value}</dd>
          </div>
        ))}
      {v.masked_id ? (
        <div className="contents">
          <dt className="text-fg-subtle">{t("engage.inbox.details.maskedId")}</dt>
          <dd className="text-right font-mono">{v.masked_id}</dd>
        </div>
      ) : null}
    </dl>
  );
}

export function LeadBlock({ detail }: { detail: ConversationDetail }) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const lead = detail.lead;
  if (!lead) return null;
  const rows: [string, string | null, boolean][] = [
    [t("engage.inbox.details.mobile"), lead.mobile_masked, true],
    [t("engage.inbox.details.email"), lead.email, false],
    [t("engage.inbox.details.interest"), lead.interest, false],
  ];
  return (
    <div className="rounded-lg border border-line bg-surface-2 p-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold">{t("engage.inbox.details.lead")}</span>
        <AppLink href={consoleRoutes.leads(summary.slug, lead.id)} className="text-[11px] font-semibold text-primary-ink hover:underline">
          {t("engage.inbox.details.openLead")}
        </AppLink>
      </div>
      <dl className="mt-2 space-y-1 text-xs">
        {rows
          .filter((r) => r[1])
          .map(([label, value, mono]) => (
            <div key={label} className="flex justify-between gap-2">
              <dt className="text-fg-subtle">{label}</dt>
              <dd className={cn("truncate", mono && "font-mono")}>{value}</dd>
            </div>
          ))}
        <div className="flex justify-between">
          <dt className="text-fg-subtle">{t("engage.inbox.details.stage")}</dt>
          <dd>
            <Badge tone="brand">{lead.stage}</Badge>
          </dd>
        </div>
      </dl>
    </div>
  );
}

export function MiniStats({ detail, now }: { detail: ConversationDetail; now: number }) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const sla = slaState(detail.sla, now);
  const confidence = detail.avg_confidence;
  const slaText =
    sla.kind === "waiting" ? t("engage.inbox.details.slaWaiting", { time: formatMinutes(sla.minutes) }) : sla.kind === "replied" ? t("engage.inbox.details.slaReplied", { time: formatMinutes(sla.minutes) }) : t("engage.inbox.details.slaNone");
  const overdue = sla.kind !== "none" && sla.overdue;
  return (
    <div className="grid grid-cols-3 gap-2 text-center">
      <div className="rounded-md bg-surface-2 py-2">
        <div className="text-[10px] font-semibold text-fg-subtle">{t("engage.inbox.details.messages")}</div>
        <div className="text-sm font-extrabold">{detail.conversation.message_count}</div>
      </div>
      <div className="rounded-md bg-surface-2 py-2">
        <div className="text-[10px] font-semibold text-fg-subtle">{t("engage.inbox.details.confidence")}</div>
        <div className={cn("text-sm font-extrabold", confidence !== null && confidence < 0.6 && "text-warning")}>{fmt.percent(confidence)}</div>
      </div>
      <div className="rounded-md bg-surface-2 px-1 py-2" data-testid="sla-timer">
        <div className="text-[10px] font-semibold text-fg-subtle">{t("engage.inbox.details.sla")}</div>
        <div className={cn("inline-flex items-center gap-1 text-xs font-extrabold", overdue && "text-danger")}>
          {overdue ? <Icon name="alert-triangle" className="size-3" /> : <Icon name="clock" className="size-3" />}
          {slaText}
        </div>
        {overdue ? <div className="text-[10px] text-danger">{sla.kind === "waiting" && sla.escalated ? t("engage.inbox.details.slaEscalated") : t("engage.inbox.details.slaOverdue", { minutes: detail.sla.reminder_minutes })}</div> : null}
      </div>
    </div>
  );
}
